import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { Mail, Github, Globe, FileText, Sparkles, CheckCircle2 } from 'lucide-react';

interface SlideProps {
  onRestart?: () => void;
  onPrint?: () => void;
}

export const Slide12Contact: React.FC<SlideProps> = ({ onRestart, onPrint }) => {
  return (
    <DeckSlideWrapper
      slideNumber={12}
      totalSlides={12}
      category="Next Steps &amp; Contact"
      title="Join Us in Securing the Enterprise AI Frontier"
      subtitle="We are actively engaging with visionary investors who understand the critical infrastructure layer of enterprise AI."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-5 items-stretch">
        {/* Left Direct Next Steps */}
        <div className="lg:col-span-7 flex flex-col justify-between p-3.5 sm:p-5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm space-y-3 sm:space-y-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-mono font-semibold bg-gold-500/15 text-gold-800 dark:text-gold-400 border border-gold-500/30 mb-2 sm:mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              <span>ROUND OPEN // SEED ALLOCATION</span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-1.5 sm:mb-2">
              Let&apos;s Schedule a 20-Min Deep Dive
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              We would love to walk you through our live in-memory DLP benchmarks, architecture implementation, and customer pipeline.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 font-mono text-xs">
            <a
              href="mailto:investors@workouse.com?subject=Workouse%20AI%20Seed%20Deck%20Inquiry"
              className="p-2.5 sm:p-3 rounded-xl bg-slate-100/90 dark:bg-navy-900 hover:border-gold-500 border border-slate-200 dark:border-navy-800 flex items-center gap-2.5 transition-all text-slate-900 dark:text-white group min-h-[44px]"
            >
              <div className="w-8 h-8 rounded-lg bg-gold-500/15 text-gold-700 dark:text-gold-500 flex items-center justify-center shrink-0 group-hover:bg-gold-500 group-hover:text-navy-950 transition-colors">
                <Mail className="w-4 h-4" />
              </div>
              <div className="truncate">
                <span className="text-[10px] text-slate-500 block font-sans">DIRECT EMAIL</span>
                <span className="font-bold truncate">investors@workouse.com</span>
              </div>
            </a>

            <a
              href="https://ai-gateway.workouse.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 sm:p-3 rounded-xl bg-slate-100/90 dark:bg-navy-900 hover:border-gold-500 border border-slate-200 dark:border-navy-800 flex items-center gap-2.5 transition-all text-slate-900 dark:text-white group min-h-[44px]"
            >
              <div className="w-8 h-8 rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-500 flex items-center justify-center shrink-0 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                <Globe className="w-4 h-4" />
              </div>
              <div className="truncate">
                <span className="text-[10px] text-slate-500 block font-sans">LIVE SITE</span>
                <span className="font-bold truncate">ai-gateway.workouse.com</span>
              </div>
            </a>

            <a
              href="https://github.com/workouse"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 sm:p-3 rounded-xl bg-slate-100/90 dark:bg-navy-900 hover:border-gold-500 border border-slate-200 dark:border-navy-800 flex items-center gap-2.5 transition-all text-slate-900 dark:text-white group min-h-[44px]"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-500/15 text-slate-700 dark:text-slate-400 flex items-center justify-center shrink-0 group-hover:bg-slate-800 group-hover:text-white transition-colors">
                <Github className="w-4 h-4" />
              </div>
              <div className="truncate">
                <span className="text-[10px] text-slate-500 block font-sans">OPEN CORE</span>
                <span className="font-bold truncate">github.com/workouse</span>
              </div>
            </a>

            <div className="p-2.5 sm:p-3 rounded-xl bg-slate-100/90 dark:bg-navy-900 border border-slate-200 dark:border-navy-800 flex items-center gap-2.5 text-slate-900 dark:text-white min-h-[44px]">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-500 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <div>
                <span className="text-[10px] text-slate-500 block font-sans">DOCS &amp; SPECS</span>
                <span className="font-bold">Architecture v1.0</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 pt-1 sm:pt-2">
            {onRestart && (
              <button
                onClick={onRestart}
                className="px-3.5 py-2 rounded-xl bg-slate-200 dark:bg-navy-800 hover:bg-slate-300 dark:hover:bg-navy-700 text-slate-800 dark:text-slate-200 font-mono text-xs font-semibold transition-all min-h-[36px]"
              >
                Restart Presentation
              </button>
            )}
            {onPrint && (
              <button
                onClick={onPrint}
                className="px-3.5 py-2 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 font-mono text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 min-h-[36px]"
              >
                <FileText className="w-3.5 h-3.5" />
                <span>Export PDF</span>
              </button>
            )}
          </div>
        </div>

        {/* Right Summary Cards */}
        <div className="lg:col-span-5 flex flex-col justify-between p-3.5 sm:p-5 rounded-2xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 shadow-surface space-y-3 sm:space-y-4">
          <div className="border-b border-slate-800 dark:border-navy-800 pb-2.5 sm:pb-3">
            <h4 className="font-mono text-xs font-bold text-gold-400 uppercase tracking-wider">
              Investment Highlights Recap
            </h4>
          </div>

          <div className="space-y-2 sm:space-y-2.5 text-[11px] sm:text-xs">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span><strong>Mission-Critical Category:</strong> Zero-Trust Data Sovereignty for enterprise AI.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span><strong>High ACV &amp; Margins:</strong> 88%+ software gross margins with $24k–$120k enterprise contracts.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span><strong>Deep Tech Moat:</strong> Turn-level ephemeral in-memory vault + Haskell parsing worker.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <span><strong>Urgent Catalysts:</strong> EU AI Act, CISO security mandates, and LLM budget blowouts.</span>
            </div>
          </div>

          <div className="p-2.5 sm:p-3 rounded-xl bg-slate-950 dark:bg-navy-900 border border-slate-800 dark:border-navy-800 text-center font-mono text-[10px] sm:text-xs text-slate-400">
            Thank you for your consideration.<br />
            <strong className="text-white">Workouse AI Inc. — Confidential &amp; Proprietary</strong>
          </div>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
